import { useEffect, useState } from "react";
import axios from "axios";
import { ScoreInfo } from "@/components/mai/Explain";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { BUCKET_STYLES } from "@/components/mai/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bar = ({ label, value, color, invert }) => (
  <div>
    <div className="flex justify-between items-center mb-1">
      <span className="overline-label text-slate-400">{label}</span>
      <span className="font-mono-data text-xs font-medium">{value}{invert && <span className="text-slate-400"> (lower = easier)</span>}</span>
    </div>
    <div className="h-1.5 bg-slate-100 w-full">
      <div className="h-full" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);

export default function MAIScorePage() {
  const { productId } = useProduct();
  const [scores, setScores] = useState(null);

  useEffect(() => {
    setScores(null);
    axios.get(`${API}/mai-scores/${productId}`).then((r) => setScores(r.data)).catch(console.error);
  }, [productId]);

  return (
    <PageShell pageId="mai-score" overline="Flagship KPI · Composite Metric" title="MAI Score — Market Access Intelligence Score">
      {!scores ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Computing MAI Scores…</div>
      ) : (
        <>
          <p className="text-sm text-slate-500 max-w-3xl -mt-2">
            Composite of Evidence Readiness (40%), Reimbursement Readiness (35%) and Market Complexity (25%, inverted). Ranked launch attractiveness for the current evidence portfolio. <ScoreInfo scoreId="mai_composite" />
          </p>
          <div data-testid="mai-score-list" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {scores.map((s, i) => {
              const bucket = BUCKET_STYLES[s.bucket];
              return (
                <div key={s.country_id} data-testid={`mai-score-${s.country_id}`} className="bg-white border border-slate-200 p-6 flex gap-6 stagger-in" style={{ animationDelay: `${i * 50}ms`, borderTop: `3px solid ${bucket.color}` }}>
                  <div className="shrink-0 text-center w-20">
                    <p className="overline-label text-slate-400">Rank {s.rank}</p>
                    <p className="font-heading font-extrabold text-5xl tracking-tight mt-1" style={{ color: bucket.color }}>{s.composite}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">MAI Score</p>
                  </div>
                  <div className="flex-1 min-w-0 space-y-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{s.flag}</span>
                      <p className="font-heading font-bold text-lg tracking-tight">{s.name}</p>
                      <span className={`ml-auto text-[10px] uppercase tracking-widest font-semibold px-2 py-1 ${bucket.badge}`}>{s.code}</span>
                    </div>
                    <Bar label="Evidence Readiness" value={s.evidence_readiness} color="#047857" />
                    <Bar label="Reimbursement Readiness" value={s.reimbursement_readiness} color="#1D4ED8" />
                    <Bar label="Market Complexity" value={s.market_complexity} color="#B45309" invert />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </PageShell>
  );
}
